import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { fmtDateTime } from '../../Utils/format';
import {
  Box,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { BarChart as ChartIcon, TableChart as TableIcon } from '@mui/icons-material';

// ── Constants ─────────────────────────────────────────────────────────────────

const GRANULARITIES = [
  { value: 'FIFTEEN_MIN', label: '15 min' },
  { value: 'THIRTY_MIN', label: '30 min' },
  { value: 'HOURLY', label: '1h' },
  { value: 'DAILY', label: 'Diário' },
  { value: 'WEEKLY', label: 'Semanal' },
];

const ALL_SERIES = [
  { value: 'counts', labelKey: 'chart.overlayCounts', defaultLabel: 'Contagens' },
  { value: 'gender', labelKey: 'chart.overlayGender', defaultLabel: 'Género' },
  { value: 'age', labelKey: 'chart.overlayAge', defaultLabel: 'Idade' },
  { value: 'dwell', labelKey: 'chart.overlayDwell', defaultLabel: 'Tempo de Permanência' },
];

const SERIES_TODAY = 'today';
const SERIES_YESTERDAY = 'yesterday';

const FIELD_COLORS = {
  totalInCount: '#1976d2',
  totalOutCount: '#ef6c00',
  totalMaleCount: '#0288d1',
  totalFemaleCount: '#e91e63',
  totalChildCount: '#ff9800',
  totalAdultCount: '#4caf50',
  totalElderCount: '#9c27b0',
  avgDwellSeconds: '#00bcd4',
};

const parseDateMs = str => {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  if (str instanceof Date) return str.getTime();
  const s = String(str);
  const iso = !s.endsWith('Z') && !s.includes('+') && !s.includes('-')
    ? s.replace(' ', 'T') + 'Z'
    : s;
  return new Date(iso).getTime();
};

const createZeroPoint = (periodStr, extraFields = {}) => ({
  period: periodStr,
  totalInCount: 0,
  totalOutCount: 0,
  totalMaleCount: 0,
  totalFemaleCount: 0,
  totalChildCount: 0,
  totalAdultCount: 0,
  totalElderCount: 0,
  avgDwellSeconds: 0,
  ...extraFields,
});

const fillTodayComparisonGaps = (data, compareData) => {
  const toHour = period => {
    const ms = parseDateMs(period);
    const dt = new Date(ms);
    return Math.round((dt.getUTCHours() + dt.getUTCMinutes() / 60) * 100) / 100;
  };

  const toHourLabel = h => {
    const mins = Math.floor((h % 1) * 60);
    return `${Math.floor(h)}:${mins < 10 ? '0' : ''}${mins}`;
  };

  // 1. Today's series
  const rawTodayPoints = (data || []).map(d => ({
    ...d,
    hour: toHour(d.period),
    series: SERIES_TODAY,
  }));

  // Determine max recorded hour for today
  let maxTodayHour = 0;
  rawTodayPoints.forEach(pt => {
    if ((pt.totalInCount || 0) > 0 || (pt.totalOutCount || 0) > 0) {
      if (pt.hour > maxTodayHour) maxTodayHour = pt.hour;
    } else if (pt.hour > maxTodayHour) {
      maxTodayHour = Math.max(maxTodayHour, pt.hour);
    }
  });

  const todayMap = new Map();
  rawTodayPoints.forEach(pt => todayMap.set(pt.hour, pt));

  let step = 1;
  if (rawTodayPoints.length > 1) {
    const diffs = [];
    for (let i = 1; i < rawTodayPoints.length; i++) {
      const diff = rawTodayPoints[i].hour - rawTodayPoints[i - 1].hour;
      if (diff > 0 && diff <= 1) diffs.push(diff);
    }
    if (diffs.length > 0) {
      const minDiff = Math.min(...diffs);
      if (minDiff <= 0.25) step = 0.25;
      else if (minDiff <= 0.5) step = 0.5;
    }
  }

  const todayDateStr = (() => {
    if (rawTodayPoints.length > 0 && rawTodayPoints[0].period) {
      const ms = parseDateMs(rawTodayPoints[0].period);
      if (ms > 0) {
        return new Date(ms).toISOString().substring(0, 10);
      }
    }
    return new Date().toISOString().substring(0, 10);
  })();

  const todayPoints = [];
  for (let h = 0; h <= maxTodayHour; h += step) {
    const roundedH = Math.round(h * 100) / 100;
    const hInt = Math.floor(roundedH);
    const mInt = Math.round((roundedH % 1) * 60);
    const pad = n => String(n).padStart(2, '0');
    const periodIso = `${todayDateStr}T${pad(hInt)}:${pad(mInt)}:00.000Z`;

    if (todayMap.has(roundedH)) {
      const existing = todayMap.get(roundedH);
      todayPoints.push({
        ...existing,
        period: existing.period || periodIso,
        hourLabel: toHourLabel(roundedH),
        series: SERIES_TODAY,
      });
    } else {
      todayPoints.push(createZeroPoint(periodIso, {
        hour: roundedH,
        hourLabel: toHourLabel(roundedH),
        series: SERIES_TODAY,
      }));
    }
  }

  // 2. Yesterday's series (always full 24h baseline 0..23)
  const rawYesterdayPoints = (compareData || []).map(d => ({
    ...d,
    hour: toHour(d.period),
    series: SERIES_YESTERDAY,
  }));

  const yesterdayMap = new Map();
  rawYesterdayPoints.forEach(pt => yesterdayMap.set(pt.hour, pt));

  const yesterdayDateStr = (() => {
    if (rawYesterdayPoints.length > 0 && rawYesterdayPoints[0].period) {
      const ms = parseDateMs(rawYesterdayPoints[0].period);
      if (ms > 0) {
        return new Date(ms).toISOString().substring(0, 10);
      }
    }
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().substring(0, 10);
  })();

  const yesterdayPoints = [];
  for (let h = 0; h <= 23; h += step) {
    const roundedH = Math.round(h * 100) / 100;
    const hInt = Math.floor(roundedH);
    const mInt = Math.round((roundedH % 1) * 60);
    const pad = n => String(n).padStart(2, '0');
    const periodIso = `${yesterdayDateStr}T${pad(hInt)}:${pad(mInt)}:00.000Z`;

    if (yesterdayMap.has(roundedH)) {
      const existing = yesterdayMap.get(roundedH);
      yesterdayPoints.push({
        ...existing,
        period: existing.period || periodIso,
        hourLabel: toHourLabel(roundedH),
        series: SERIES_YESTERDAY,
      });
    } else {
      yesterdayPoints.push(createZeroPoint(periodIso, {
        hour: roundedH,
        hourLabel: toHourLabel(roundedH),
        series: SERIES_YESTERDAY,
      }));
    }
  }

  return [...todayPoints, ...yesterdayPoints];
};

const fillMultiDayGaps = (data, granularity, startDate, endDate) => {
  if (!Array.isArray(data) || data.length === 0) {
    return data || [];
  }

  const sorted = data.slice().sort((a, b) => parseDateMs(a.period) - parseDateMs(b.period));
  const rangeStartMs = startDate ? parseDateMs(startDate) : 0;
  const startMs = rangeStartMs > 0 ? rangeStartMs : parseDateMs(sorted[0].period);
  const rawEndMs = sorted.length > 0 ? parseDateMs(sorted[sorted.length - 1].period) : Date.now();
  const rangeEndMs = endDate ? parseDateMs(endDate) : 0;
  const endLimitMs = rangeEndMs > 0 ? rangeEndMs : rawEndMs;
  const nowMs = Date.now();
  const endMs = Math.min(Math.max(rawEndMs, endLimitMs), nowMs);

  if (startMs <= 0 || endMs < startMs) {
    return data;
  }

  let stepMs = 3600 * 1000;
  if (granularity === 'FIFTEEN_MIN') {
    stepMs = 15 * 60 * 1000;
  } else if (granularity === 'THIRTY_MIN') {
    stepMs = 30 * 60 * 1000;
  } else if (granularity === 'DAILY' || granularity === 'DAY') {
    stepMs = 24 * 3600 * 1000;
  }

  const dataMap = new Map();
  sorted.forEach(d => {
    const ms = parseDateMs(d.period);
    const bucket = Math.floor(ms / stepMs) * stepMs;
    dataMap.set(bucket, d);
  });

  const filled = [];
  const startBucket = Math.floor(startMs / stepMs) * stepMs;
  const endBucket = Math.floor(endMs / stepMs) * stepMs;

  for (let currentMs = startBucket; currentMs <= endBucket; currentMs += stepMs) {
    if (dataMap.has(currentMs)) {
      filled.push(dataMap.get(currentMs));
    } else {
      const periodIso = new Date(currentMs).toISOString();
      filled.push(createZeroPoint(periodIso));
    }
  }

  return filled;
};

// ── Tooltip helpers ───────────────────────────────────────────────────────────

const TOOLTIP_ID = 'vtc-tooltip';

const getTooltipEl = () => {
  let el = document.getElementById(TOOLTIP_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = TOOLTIP_ID;
    el.style.cssText = [
      'position:fixed', 'background:#fff', 'border:1px solid #e0e0e0',
      'border-radius:6px', 'padding:8px 12px', 'box-shadow:0 2px 8px rgba(0,0,0,0.15)',
      'font-size:12px', 'line-height:1.6', 'pointer-events:none', 'z-index:9999', 'min-width:160px',
    ].join(';');
    document.body.appendChild(el);
  }
  return el;
};

const positionTooltip = (el, event) => {
  const tooltipW = el.offsetWidth || 210;
  const tooltipH = el.offsetHeight || 150;
  const x = Math.min(event.clientX + 14, window.innerWidth - tooltipW - 8);
  const spaceBelow = window.innerHeight - event.clientY;
  const y = spaceBelow > tooltipH + 20 ? event.clientY - 10 : event.clientY - tooltipH - 14;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.display = 'block';
};

const rowHtml = r =>
  '<div style="display:flex;align-items:center;gap:6px;margin:1px 0">'
  + `<span style="width:8px;height:8px;border-radius:50%;flex-shrink:0;background:${r.color || 'transparent'}"></span>`
  + `<span style="flex:1;color:#555;white-space:nowrap">${r.label}</span>`
  + `<span style="font-weight:600;margin-left:12px;color:#111">${r.value ?? '-'}</span>`
  + '</div>';

// ── Vega-Lite spec builder ────────────────────────────────────────────────────

const buildSpec = ({ chartData, overlays, isToday, granularity, chartHeight, t }) => {
  const isSubDaily = granularity === 'FIFTEEN_MIN' || granularity === 'THIRTY_MIN' || granularity === 'HOURLY' || granularity === 'HOUR';
  const xFormat = isSubDaily ? '%d/%m %H:%M' : '%d/%m';

  const xEnc = isToday
    ? {
        field: 'hour',
        type: 'quantitative',
        axis: {
          labelExpr: "floor(datum.value) + ':' + (datum.value % 1 * 60 < 10 ? '0' : '') + floor(datum.value % 1 * 60)",
          tickCount: 12,
          title: null,
        },
      }
    : {
        field: 'period',
        type: 'temporal',
        axis: { format: xFormat, tickCount: 8, title: null },
      };

  const entriesLabel = t('dashboard:chart.entries', 'Entradas');
  const exitsLabel = t('dashboard:chart.exits', 'Saídas');

  const showCounts = overlays.includes('counts');
  const showGender = overlays.includes('gender');
  const showAge = overlays.includes('age');
  const showDwell = overlays.includes('dwell');

  const overlayTooltip = [];
  if (showCounts) {
    overlayTooltip.push({ field: 'totalInCount', title: entriesLabel });
    overlayTooltip.push({ field: 'totalOutCount', title: exitsLabel });
  }
  if (showGender) {
    overlayTooltip.push({ field: 'totalMaleCount', title: t('dashboard:kpi.masculine', 'Masculino') });
    overlayTooltip.push({ field: 'totalFemaleCount', title: t('dashboard:kpi.feminine', 'Feminino') });
  }
  if (showAge) {
    overlayTooltip.push({ field: 'totalChildCount', title: t('dashboard:kpi.child', 'Crianças') });
    overlayTooltip.push({ field: 'totalAdultCount', title: t('dashboard:kpi.adult', 'Adultos') });
    overlayTooltip.push({ field: 'totalElderCount', title: t('dashboard:kpi.elder', 'Séniores') });
  }
  if (showDwell) {
    overlayTooltip.push({ field: 'avgDwellSeconds', title: t('dashboard:chart.dwellSeconds', 'Permanência (s)') });
  }

  const tooltip = isToday
    ? [
        { field: 'hourLabel', title: t('dashboard:chart.hour', 'Hora') },
        ...overlayTooltip,
      ]
    : [
        { field: 'period', type: 'temporal', title: t('dashboard:chart.period', 'Período'), format: '%d/%m/%Y %H:%M' },
        ...overlayTooltip,
      ];

  const todayFilter = isToday ? [{ filter: `datum.series === '${SERIES_TODAY}'` }] : [];
  const countLayers = [];

  if (showCounts) {
    // Yesterday comparison — grey line (today mode only)
    if (isToday) {
      countLayers.push({
        transform: [{ filter: `datum.series === '${SERIES_YESTERDAY}'` }],
        mark: { type: 'line', color: '#bdbdbd', strokeWidth: 1.5, opacity: 0.75 },
        encoding: {
          y: { field: 'totalInCount', type: 'quantitative' },
          tooltip: [
            { field: 'hourLabel', title: t('dashboard:chart.hour', 'Hora') },
            { field: 'totalInCount', title: `${t('dashboard:chart.yesterday', 'Ontem')} (${entriesLabel})` },
          ],
        },
      });
    }

    // Entries — blue area
    countLayers.push({
      transform: todayFilter,
      mark: {
        type: 'area',
        color: '#1976d2',
        opacity: 0.35,
        line: { color: '#1976d2', strokeWidth: 2 },
      },
      encoding: {
        y: { field: 'totalInCount', type: 'quantitative' },
        tooltip,
      },
    });

    // Exits — dashed orange line
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#ef6c00', strokeDash: [5, 3], strokeWidth: 2 },
      encoding: {
        y: { field: 'totalOutCount', type: 'quantitative' },
        tooltip,
      },
    });
  }

  if (showGender) {
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#0288d1', strokeWidth: 1.5, strokeDash: [3, 1] },
      encoding: { y: { field: 'totalMaleCount', type: 'quantitative' }, tooltip },
    });
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#e91e63', strokeWidth: 1.5, strokeDash: [3, 1] },
      encoding: { y: { field: 'totalFemaleCount', type: 'quantitative' }, tooltip },
    });
  }

  if (showAge) {
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#ff9800', strokeWidth: 1.5, strokeDash: [3, 1] },
      encoding: { y: { field: 'totalChildCount', type: 'quantitative' }, tooltip },
    });
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#4caf50', strokeWidth: 1.5, strokeDash: [3, 1] },
      encoding: { y: { field: 'totalAdultCount', type: 'quantitative' }, tooltip },
    });
    countLayers.push({
      transform: todayFilter,
      mark: { type: 'line', color: '#9c27b0', strokeWidth: 1.5, strokeDash: [3, 1] },
      encoding: { y: { field: 'totalElderCount', type: 'quantitative' }, tooltip },
    });
  }

  const countTitle = t('dashboard:chart.overlayCounts', 'Contagens');

  const axisTitleProps = {
    titleFontSize: 15,
    titleFontWeight: 600,
    titlePadding: 18,
    titleColor: '#2A2B2D',
    titleFont: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    labelFontSize: 12.5,
    labelPadding: 8,
    labelColor: '#60656B',
    labelFont: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  };

  let layers = [];
  let resolve = {};

  if (countLayers.length > 0 && showDwell) {
    // Dual axis: group all count layers to share primary left scale; dwell has independent right scale
    layers = [
      {
        layer: countLayers,
        encoding: {
          y: {
            axis: {
              title: countTitle,
              orient: 'left',
              ...axisTitleProps,
            },
          },
        },
      },
      {
        transform: todayFilter,
        mark: { type: 'line', color: '#00bcd4', strokeWidth: 2 },
        encoding: {
          y: {
            field: 'avgDwellSeconds',
            type: 'quantitative',
            axis: {
              title: t('dashboard:chart.dwellSeconds', 'Permanência (s)'),
              orient: 'right',
              grid: false,
              ...axisTitleProps,
              titleColor: '#00838f',
            },
          },
          tooltip,
        },
      },
    ];
    resolve = { scale: { y: 'independent' } };
  } else if (countLayers.length > 0) {
    // Single axis: all count layers share primary left scale
    layers = countLayers;
    if (layers[0]?.encoding?.y) {
      layers[0].encoding.y.axis = {
        title: countTitle,
        orient: 'left',
        ...axisTitleProps,
      };
    }
  } else if (showDwell) {
    // Only Dwell is selected: render on primary left scale
    layers = [
      {
        transform: todayFilter,
        mark: { type: 'line', color: '#00bcd4', strokeWidth: 2 },
        encoding: {
          y: {
            field: 'avgDwellSeconds',
            type: 'quantitative',
            axis: {
              title: t('dashboard:chart.dwellSeconds', 'Permanência (s)'),
              orient: 'left',
              ...axisTitleProps,
              titleColor: '#00838f',
            },
          },
          tooltip,
        },
      },
    ];
  } else {
    // Fallback if no overlays are selected
    layers = [
      {
        transform: todayFilter,
        mark: { type: 'point', opacity: 0 },
        encoding: {
          y: {
            field: 'totalInCount',
            type: 'quantitative',
            axis: {
              title: countTitle,
              orient: 'left',
              ...axisTitleProps,
            },
          },
        },
      },
    ];
  }

  return {
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    width: 'container',
    height: chartHeight,
    autosize: { type: 'fit-x', contains: 'padding' },
    data: { values: chartData },
    encoding: { x: xEnc },
    layer: layers,
    resolve,
    config: {
      view: { stroke: 'transparent', continuousWidth: 400 },
      background: 'transparent',
      axis: {
        gridOpacity: 0.18,
        labelFontSize: 12.5,
        titleFontSize: 15,
        labelFont: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        titleFont: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        titleFontWeight: 600,
        titleColor: '#2A2B2D',
        labelColor: '#60656B',
        titlePadding: 18,
        labelPadding: 8,
      },
      axisY: {
        titleFontSize: 15,
        titleFontWeight: 600,
        titlePadding: 18,
        labelFontSize: 12.5,
        labelPadding: 8,
      },
      axisX: {
        titleFontSize: 13,
        labelFontSize: 12,
        labelPadding: 8,
      },
    },
  };
};

// ── Component ─────────────────────────────────────────────────────────────────

const VegaTrafficChart = ({
  data = [],
  compareData = [],
  granularity = 'HOURLY',
  onGranularityChange = null,
  title = '',
  subtitle = '',
  height = 430,
  isToday = false,
  period = null,
  startDate = null,
  endDate = null,
  hideDwellOverlay = false,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const vegaViewRef = useRef(null);
  const selectionDivRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0 });
  const [viewMode, setViewMode] = useState('chart');
  const [overlays, setOverlays] = useState(['counts']);
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const [zoomRange, setZoomRange] = useState(null);
  const chartHeight = height - 80;
  const availableSeries = useMemo(() => {
    return hideDwellOverlay ? ALL_SERIES.filter(s => s.value !== 'dwell') : ALL_SERIES;
  }, [hideDwellOverlay]);

  const effectivePeriod = useMemo(() => {
    if (period && period !== 'custom') return period;
    if (isToday) return 'today';
    if (startDate && endDate) {
      const diffMs = Math.abs(new Date(endDate).getTime() - new Date(startDate).getTime());
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays <= 1) return 'today';
      if (diffDays <= 7) return '7d';
    }
    return 'custom';
  }, [period, isToday, startDate, endDate]);

  const availableGranularities = useMemo(() => {
    if (effectivePeriod === 'today') {
      return GRANULARITIES.filter(g => g.value !== 'DAILY' && g.value !== 'WEEKLY');
    }
    if (effectivePeriod === '7d') {
      return GRANULARITIES.filter(g => g.value !== 'WEEKLY');
    }
    return GRANULARITIES;
  }, [effectivePeriod]);

  useEffect(() => {
    if (onGranularityChange && availableGranularities.length > 0) {
      const isValid = availableGranularities.some(g => g.value === granularity);
      if (!isValid) {
        onGranularityChange('HOURLY');
      }
    }
  }, [effectivePeriod, granularity, availableGranularities, onGranularityChange]);

  // Reset zoom whenever temporal filters, period, date range, granularity, or data change
  useEffect(() => {
    setZoomRange(null);
  }, [period, isToday, startDate, endDate, granularity, data]);

  // Filter data points so records prior to startDate do not leak in
  const filteredData = useMemo(() => {
    if (isToday || !startDate || !Array.isArray(data)) {
      return data;
    }
    const startMs = parseDateMs(startDate);
    const startDayStr = (() => {
      if (typeof startDate === 'string' && startDate.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(startDate)) {
        return startDate.substring(0, 10);
      }
      const d = startDate instanceof Date ? startDate : new Date(startDate);
      if (isNaN(d.getTime())) {
        return '';
      }
      return d.toISOString().substring(0, 10);
    })();

    return data.filter((d) => {
      const ms = parseDateMs(d.period);
      if (ms < startMs) {
        return false;
      }
      if (startDayStr) {
        const rowDayStr = String(d.period || '').substring(0, 10);
        if (rowDayStr && rowDayStr < startDayStr) {
          return false;
        }
      }
      return true;
    });
  }, [data, isToday, startDate]);

  // Pre-process: fractional hour + series label for today comparison mode & time-series gap filling
  const chartData = useMemo(() => {
    if (isToday) {
      return fillTodayComparisonGaps(filteredData, compareData);
    }
    return fillMultiDayGaps(filteredData, granularity, startDate, endDate);
  }, [filteredData, compareData, isToday, granularity, startDate, endDate]);

  // Filter chartData by active zoom range (pixel→value conversion happens in handlers)
  const visibleData = useMemo(() => {
    if (!zoomRange) return chartData;
    const [lo, hi] = zoomRange;
    const loMs = Number(lo);
    const hiMs = Number(hi);
    return chartData.filter(d => {
      const val = isToday ? d.hour : parseDateMs(d.period);
      return val >= loMs && val <= hiMs;
    });
  }, [chartData, zoomRange, isToday]);

  // Legend items driven by current overlays + isToday
  const legendItems = useMemo(() => {
    const items = [];
    if (overlays.includes('counts')) {
      items.push({ label: t('dashboard:chart.entries'), color: FIELD_COLORS.totalInCount });
      items.push({ label: t('dashboard:chart.exits'), color: FIELD_COLORS.totalOutCount });
      if (isToday) items.push({ label: t('dashboard:chart.yesterday'), color: '#bdbdbd' });
    }
    if (overlays.includes('gender')) {
      items.push({ label: t('dashboard:kpi.masculine'), color: FIELD_COLORS.totalMaleCount });
      items.push({ label: t('dashboard:kpi.feminine'), color: FIELD_COLORS.totalFemaleCount });
    }
    if (overlays.includes('age')) {
      items.push({ label: t('dashboard:kpi.child'), color: FIELD_COLORS.totalChildCount });
      items.push({ label: t('dashboard:kpi.adult'), color: FIELD_COLORS.totalAdultCount });
      items.push({ label: t('dashboard:kpi.elder'), color: FIELD_COLORS.totalElderCount });
    }
    if (overlays.includes('dwell')) {
      items.push({ label: t('dashboard:chart.overlayDwell'), color: FIELD_COLORS.avgDwellSeconds });
    }
    return items;
  }, [isToday, overlays, t]);

  // Embed Vega chart
  useEffect(() => {
    if (viewMode !== 'chart' || !chartRef.current || !visibleData.length) return;

    const { vegaEmbed } = window;
    if (!vegaEmbed) {
      console.error('[VegaTrafficChart] window.vegaEmbed not defined');
      return;
    }

    const vlSpec = buildSpec({
      chartData: visibleData,
      overlays,
      isToday,
      granularity,
      chartHeight,
      t,
    });

    vegaEmbed(chartRef.current, vlSpec, {
      actions: false,
      renderer: 'canvas',
    }).then(result => {
      if (vegaViewRef.current) vegaViewRef.current.finalize();
      vegaViewRef.current = result.view;
    }).catch(err => {
      console.error('[VegaTrafficChart] vegaEmbed failed:', err);
    });

    return () => {
      vegaViewRef.current?.finalize();
    };
  }, [viewMode, visibleData, overlays, isToday, granularity, chartHeight, t]);

  // Finalize view + remove tooltip div on unmount
  useEffect(() => () => {
    vegaViewRef.current?.finalize();
    document.getElementById(TOOLTIP_ID)?.remove();
  }, []);

  // ── Nearest-x tooltip (fires on any mouse position over the chart area) ──────
  const showNearestTooltip = e => {
    const view = vegaViewRef.current;
    if (!view || !visibleData.length) return;

    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    try {
      const padding = typeof view.padding === 'function' ? view.padding() : (view.padding || {});
      const padLeft = (typeof view.origin === 'function' ? view.origin()[0] : null) ?? padding.left ?? 50;
      const canvasMouseX = mouseX - padLeft;

      const xScale = view.scale('x');
      if (!xScale) return;

      const mouseXVal = xScale.invert(canvasMouseX);
      const mouseMs = mouseXVal instanceof Date ? mouseXVal.getTime() : Number(mouseXVal);
      if (isNaN(mouseMs)) return;

      // Find datum with closest x value to mouse position
      let minDiff = Infinity;
      let nearestXVal = null;
      visibleData.forEach(d => {
        const dVal = isToday ? d.hour : parseDateMs(d.period);
        const diff = Math.abs(dVal - mouseMs);
        if (diff < minDiff) {
          minDiff = diff;
          nearestXVal = dVal;
        }
      });

      if (nearestXVal === null) return;

      // Collect all datums at nearest x (today mode: Hoje + Ontem; non-today: single)
      const matched = visibleData.filter(d => {
        const dVal = isToday ? d.hour : parseDateMs(d.period);
        return Math.abs(dVal - nearestXVal) < 1e-9;
      });
      const hoje = isToday ? matched.find(d => d.series === SERIES_TODAY) : matched[0];
      const ontem = isToday ? matched.find(d => d.series === SERIES_YESTERDAY) : null;
      const d = hoje || ontem;
      if (!d) return;

      const rows = [];
      if (isToday) {
        rows.push({ label: t('dashboard:chart.hour'), value: d.hourLabel, color: null });
      } else {
        rows.push({
          label: t('dashboard:chart.period'),
          value: fmtDateTime(d.period, true),
          color: null,
        });
      }
      if (overlays.includes('counts')) {
        rows.push({ label: t('dashboard:chart.entries'), value: d.totalInCount, color: FIELD_COLORS.totalInCount });
        rows.push({ label: t('dashboard:chart.exits'), value: d.totalOutCount, color: FIELD_COLORS.totalOutCount });
        if (ontem) {
          rows.push({ label: `${t('dashboard:chart.yesterday')} (${t('dashboard:chart.entries')})`, value: ontem.totalInCount, color: '#bdbdbd' });
        }
      }
      if (overlays.includes('gender')) {
        rows.push({ label: t('dashboard:kpi.masculine'), value: d.totalMaleCount, color: FIELD_COLORS.totalMaleCount });
        rows.push({ label: t('dashboard:kpi.feminine'), value: d.totalFemaleCount, color: FIELD_COLORS.totalFemaleCount });
      }
      if (overlays.includes('age')) {
        rows.push({ label: t('dashboard:kpi.child'), value: d.totalChildCount, color: FIELD_COLORS.totalChildCount });
        rows.push({ label: t('dashboard:kpi.adult'), value: d.totalAdultCount, color: FIELD_COLORS.totalAdultCount });
        rows.push({ label: t('dashboard:kpi.elder'), value: d.totalElderCount, color: FIELD_COLORS.totalElderCount });
      }
      if (overlays.includes('dwell')) {
        rows.push({ label: t('dashboard:chart.dwellSeconds'), value: d.avgDwellSeconds, color: FIELD_COLORS.avgDwellSeconds });
      }

      const el = getTooltipEl();
      el.innerHTML = rows.map(rowHtml).join('');
      positionTooltip(el, e);
    } catch (_) { /* scale not ready */ }
  };

  // ── Drag-to-zoom handlers ─────────────────────────────────────────────────
  const handleMouseDown = e => {
    if (e.button !== 0) return;
    const rect = chartRef.current.getBoundingClientRect();
    dragRef.current = { active: true, startX: e.clientX - rect.left };
    const sel = selectionDivRef.current;
    if (sel) {
      sel.style.left = `${dragRef.current.startX}px`;
      sel.style.width = '0px';
      sel.style.opacity = '1';
    }
  };

  const handleMouseMove = e => {
    showNearestTooltip(e);
    if (!dragRef.current.active) return;
    const rect = chartRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const left = Math.min(dragRef.current.startX, currentX);
    const width = Math.abs(currentX - dragRef.current.startX);
    const sel = selectionDivRef.current;
    if (sel) {
      sel.style.left = `${left}px`;
      sel.style.width = `${width}px`;
    }
  };

  const handleMouseUp = e => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const sel = selectionDivRef.current;
    if (sel) sel.style.opacity = '0';
    const rect = chartRef.current.getBoundingClientRect();
    const endX = e.clientX - rect.left;
    if (Math.abs(endX - dragRef.current.startX) < 8) return;
    const view = vegaViewRef.current;
    if (!view) return;
    try {
      const padding = typeof view.padding === 'function' ? view.padding() : (view.padding || {});
      const padLeft = (typeof view.origin === 'function' ? view.origin()[0] : null) ?? padding.left ?? 50;
      const xScale = view.scale('x');
      if (!xScale) return;

      const clampX = x => Math.max(0, Math.min(view.width(), x - padLeft));
      const x1 = clampX(dragRef.current.startX);
      const x2 = clampX(endX);

      const raw1 = xScale.invert(x1);
      const raw2 = xScale.invert(x2);

      const v1 = raw1 instanceof Date ? raw1.getTime() : Number(raw1);
      const v2 = raw2 instanceof Date ? raw2.getTime() : Number(raw2);

      if (!isNaN(v1) && !isNaN(v2) && Math.abs(v2 - v1) > 0) {
        setZoomRange(v1 < v2 ? [v1, v2] : [v2, v1]);
      }
    } catch (_) { /* scale not available */ }
  };

  const handleMouseLeave = () => {
    if (dragRef.current.active) {
      dragRef.current.active = false;
      const sel = selectionDivRef.current;
      if (sel) sel.style.opacity = '0';
    }
    const el = document.getElementById(TOOLTIP_ID);
    if (el) el.style.display = 'none';
  };

  // ── Table render ──────────────────────────────────────────────────────────
  const renderTable = () => {
    const tableData = isToday
      ? chartData.filter(d => d.series !== SERIES_YESTERDAY)
      : chartData;
    const rows = [...tableData].sort((a, b) => new Date(a.period) - new Date(b.period));

    if (rows.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
          <Typography variant="body2" color="text.secondary">{t('dashboard:chart.noData')}</Typography>
        </Box>
      );
    }

    const hasCompareData = compareData && compareData.length > 0;
    const compareMap = new Map();
    if (hasCompareData) {
      compareData.forEach(cd => {
        const ms = parseDateMs(cd.period);
        const dt = new Date(ms);
        const key = isToday ? `${dt.getUTCHours()}:${dt.getUTCMinutes()}` : cd.period;
        compareMap.set(key, cd);
      });
    }

    return (
      <TableContainer sx={{ maxHeight: chartHeight, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('dashboard:chart.hourOrDate')}</TableCell>
              {overlays.includes('counts') && (
                <>
                  <TableCell align="right">{t('dashboard:chart.entries')}</TableCell>
                  {hasCompareData && isToday && (
                    <TableCell align="right">{t('dashboard:chart.yesterday')} ({t('dashboard:chart.entries')})</TableCell>
                  )}
                  <TableCell align="right">{t('dashboard:chart.exits')}</TableCell>
                  {hasCompareData && isToday && (
                    <TableCell align="right">{t('dashboard:chart.yesterday')} ({t('dashboard:chart.exits')})</TableCell>
                  )}
                </>
              )}
              {overlays.includes('gender') && (
                <>
                  <TableCell align="right">{t('dashboard:kpi.masculine')}</TableCell>
                  <TableCell align="right">{t('dashboard:kpi.feminine')}</TableCell>
                </>
              )}
              {overlays.includes('age') && (
                <>
                  <TableCell align="right">{t('dashboard:kpi.child')}</TableCell>
                  <TableCell align="right">{t('dashboard:kpi.adult')}</TableCell>
                  <TableCell align="right">{t('dashboard:kpi.elder')}</TableCell>
                </>
              )}
              {overlays.includes('dwell') && <TableCell align="right">{t('dashboard:chart.dwellSeconds')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const ms = parseDateMs(row.period);
              const dt = new Date(ms);
              const compKey = isToday ? `${dt.getUTCHours()}:${dt.getUTCMinutes()}` : row.period;
              const compareRow = compareMap.get(compKey);

              return (
                <TableRow key={i} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {fmtDateTime(row.period, true)}
                  </TableCell>
                  {overlays.includes('counts') && (
                    <>
                      <TableCell align="right">{row.totalInCount}</TableCell>
                      {hasCompareData && isToday && (
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          {compareRow ? compareRow.totalInCount : '-'}
                        </TableCell>
                      )}
                      <TableCell align="right">{row.totalOutCount}</TableCell>
                      {hasCompareData && isToday && (
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          {compareRow ? compareRow.totalOutCount : '-'}
                        </TableCell>
                      )}
                    </>
                  )}
                  {overlays.includes('gender') && (
                    <>
                      <TableCell align="right">{row.totalMaleCount ?? '-'}</TableCell>
                      <TableCell align="right">{row.totalFemaleCount ?? '-'}</TableCell>
                    </>
                  )}
                  {overlays.includes('age') && (
                    <>
                      <TableCell align="right">{row.totalChildCount ?? '-'}</TableCell>
                      <TableCell align="right">{row.totalAdultCount ?? '-'}</TableCell>
                      <TableCell align="right">{row.totalElderCount ?? '-'}</TableCell>
                    </>
                  )}
                  {overlays.includes('dwell') && (
                    <TableCell align="right">{row.avgDwellSeconds ?? '-'}</TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {onGranularityChange && (
            <ToggleButtonGroup
              value={granularity}
              exclusive
              onChange={(_, v) => v && onGranularityChange(v)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
            >
              {availableGranularities.map(g => (
                <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          <Box onMouseLeave={() => setSelectMenuOpen(false)}>
            <Select
              size="small"
              multiple
              open={selectMenuOpen}
              onOpen={() => setSelectMenuOpen(true)}
              onClose={() => setSelectMenuOpen(false)}
              MenuProps={{
                PaperProps: {
                  onMouseLeave: () => setSelectMenuOpen(false),
                },
              }}
              value={overlays}
              onChange={e => {
                const val = e.target.value;
                setOverlays(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={selected => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('dashboard:chart.overlayNone', 'Nenhum')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {selected.map(v => {
                        const item = availableSeries.find(s => s.value === v);
                        return (
                          <Chip
                            key={v}
                            label={t(`dashboard:${item?.labelKey}`, item?.defaultLabel || v)}
                            size="small"
                            sx={{ height: 22, fontSize: '0.75rem' }}
                          />
                        );
                      })}
                    </Box>
              )}
              sx={{ fontSize: '0.8125rem', minWidth: 140, maxWidth: 320, minHeight: 32 }}
            >
              {availableSeries.map(s => (
                <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.8125rem', py: 0.5 }}>
                  <Checkbox checked={overlays.includes(s.value)} size="small" />
                  <ListItemText primary={t(`dashboard:${s.labelKey}`, s.defaultLabel)} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                </MenuItem>
              ))}
            </Select>
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 0.875, py: 0.25 } }}
          >
            <ToggleButton value="chart" title={t('dashboard:chart.viewChart')}><ChartIcon sx={{ fontSize: 18 }} /></ToggleButton>
            <ToggleButton value="table" title={t('dashboard:chart.viewTable')}><TableIcon sx={{ fontSize: 18 }} /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Content */}
      {viewMode === 'chart' ? (
        chartData.length > 0 ? (
          <>
            {/* Drag-to-zoom wrapper — captures mouse events, overlay div shows selection */}
            <Box
              sx={{ position: 'relative', userSelect: 'none', cursor: 'crosshair' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onDoubleClick={() => setZoomRange(null)}
            >
              <Box
                ref={selectionDivRef}
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  zIndex: 1,
                  background: 'rgba(25,118,210,0.12)',
                  border: '1px solid rgba(25,118,210,0.4)',
                  pointerEvents: 'none',
                  opacity: 0,
                }}
                style={{ left: 0, width: 0 }}
              />
              <Box ref={chartRef} sx={{ width: '100%', height: chartHeight }} />
            </Box>
            {/* Legend + zoom reset */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 1.5, mb: 0.5, justifyContent: 'center', alignItems: 'center' }}>
              {legendItems.map(item => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: item.color,
                      flexShrink: 0,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      letterSpacing: '0.015em',
                      color: 'text.secondary',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
              {zoomRange && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    ml: 1,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => setZoomRange(null)}
                >
                  × {t('dashboard:chart.resetZoom')}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard:chart.noData')}</Typography>
          </Box>
        )
      ) : (
        renderTable()
      )}
    </Box>
  );
};

VegaTrafficChart.propTypes = {
  data: PropTypes.array.isRequired,
  compareData: PropTypes.array,
  granularity: PropTypes.string,
  onGranularityChange: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  height: PropTypes.number,
  isToday: PropTypes.bool,
  period: PropTypes.string,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
};

export default VegaTrafficChart;
